import React, { useState, useEffect } from 'react';
import { Button, Form, Modal } from 'react-bootstrap';
import axios from 'axios';
import toast from 'react-hot-toast';
import { API_BASE_URL, getBearerToken } from '../../functions';

const UmrahAdditionalDiscount = ({ show, setShow, selectedBooking, onSuccess }) => {
  const handleClose = () => {
    setShow(false);
    setDiscountAmount('');
    setDiscountName('');
  };
  const [loading, setLoading] = useState(false);
  const [discountAmount, setDiscountAmount] = useState('');
  const [discountName, setDiscountName] = useState('');

  // Load existing discount data when modal opens
  useEffect(() => {
    if (show && selectedBooking) {
      setDiscountAmount(selectedBooking.additional_discount_amount || '');
      setDiscountName(selectedBooking.additional_discount_name || '');
    }
  }, [show, selectedBooking]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!discountAmount || parseFloat(discountAmount) <= 0) {
      toast.error('Please enter a valid discount amount');
      return;
    }

    const maxDiscount = selectedBooking?.total_amount - (selectedBooking?.package_discount_amount || 0);
    if (parseFloat(discountAmount) > maxDiscount) {
      toast.error(`Maximum additional discount allowed: PKR ${maxDiscount.toLocaleString()}`);
      return;
    }

    setLoading(true);
    const tid = toast.loading('Applying additional discount...');
    
    try {
      const response = await axios.post(`${API_BASE_URL}/umrah-booking/additional-discount`, {
        booking_id: selectedBooking?.id,
        additional_discount_amount: parseFloat(discountAmount),
        additional_discount_name: discountName
      }, {
        headers: {
          'Authorization': `Bearer ${getBearerToken()}`
        }
      });

      if (response.data.status) {
        toast.success('Additional discount applied successfully');
        handleClose();
        setDiscountAmount('');
        setDiscountName('');
        if (onSuccess) {
          onSuccess();
        }
      } else {
        toast.error(response.data.message || 'Failed to apply additional discount');
      }
    } catch (error) {
      console.error('Error applying additional discount:', error);
      toast.error(error.response?.data?.message || 'Failed to apply additional discount');
    } finally {
      setLoading(false);
      toast.dismiss(tid);
    }
  };

  const handleRemoveDiscount = async () => {
    if (!selectedBooking?.additional_discount_amount || selectedBooking.additional_discount_amount <= 0) {
      toast.error('No additional discount to remove');
      return;
    }

    setLoading(true);
    const tid = toast.loading('Removing additional discount...');
    
    try {
      const response = await axios.post(`${API_BASE_URL}/umrah-booking/additional-discount`, {
        booking_id: selectedBooking?.id,
        additional_discount_amount: 0,
        additional_discount_name: ''
      }, {
        headers: {
          'Authorization': `Bearer ${getBearerToken()}`
        }
      });

      if (response.data.status) {
        toast.success('Additional discount removed successfully');
        handleClose();
        if (onSuccess) {
          onSuccess();
        }
      } else {
        toast.error(response.data.message || 'Failed to remove additional discount');
      }
    } catch (error) {
      console.error('Error removing additional discount:', error);
      toast.error(error.response?.data?.message || 'Failed to remove additional discount');
    } finally {
      setLoading(false);
      toast.dismiss(tid);
    }
  };

  const maxDiscount = selectedBooking?.total_amount - (selectedBooking?.package_discount_amount || 0);

  return (
    <Modal show={show} onHide={handleClose} centered>
      <Modal.Header closeButton>
        <Modal.Title>{selectedBooking?.additional_discount_amount > 0 ? 'Edit Additional Discount' : 'Add Additional Discount'}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="mb-3">
          <strong>Booking ID:</strong> {selectedBooking?.id}
        </div>
        <div className="mb-3">
          <strong>Package:</strong> {selectedBooking?.umrah_package?.title}
        </div>
        <div className="mb-3">
          <strong>Subtotal:</strong> PKR {Number(selectedBooking?.total_amount ?? 0).toLocaleString()}
        </div>
        {selectedBooking?.package_discount_amount > 0 && (
          <div className="mb-3">
            <strong>Package Discount:</strong> -PKR {Number(selectedBooking?.package_discount_amount ?? 0).toLocaleString()}
          </div>
        )}
        <hr />
        <Form onSubmit={handleSubmit}>
          <Form.Group className="mb-3">
            <Form.Label>Discount Name/Reason</Form.Label>
            <Form.Control
              type="text"
              value={discountName}
              onChange={(e) => setDiscountName(e.target.value)}
              placeholder="e.g., Early Bird Discount, Group Discount, Special Offer"
              maxLength={255}
            />
            <Form.Text className="text-muted">
              Optional: Enter a name or reason for this discount
            </Form.Text>
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Additional Discount Amount (PKR)</Form.Label>
            <Form.Control
              type="number"
              min="0"
              max={maxDiscount}
              step="0.01"
              value={discountAmount}
              onChange={(e) => setDiscountAmount(e.target.value)}
              placeholder={`Maximum: PKR ${maxDiscount.toLocaleString()}`}
              required
            />
            <Form.Text className="text-muted">
              Maximum additional discount: PKR {maxDiscount.toLocaleString()}
            </Form.Text>
          </Form.Group>
        </Form>
        
        <hr />
        <div className="mb-3">
          <strong>Current Final Amount:</strong> PKR {Number(selectedBooking?.final_amount ?? selectedBooking?.total_amount ?? 0).toLocaleString()}
        </div>
      </Modal.Body>
      <Modal.Footer>
        <div className="d-flex justify-content-between w-100">
          <div>
            {selectedBooking?.additional_discount_amount > 0 && (
              <Button variant="danger" onClick={handleRemoveDiscount} disabled={loading}>
                {loading ? 'Removing...' : 'Remove Discount'}
              </Button>
            )}
          </div>
          <div>
            <Button variant="secondary" onClick={handleClose} disabled={loading} className="me-2">
              Cancel
            </Button>
            <Button variant="success" onClick={handleSubmit} disabled={loading || !discountAmount}>
              {loading ? 'Saving...' : (selectedBooking?.additional_discount_amount > 0 ? 'Update Discount' : 'Apply Discount')}
            </Button>
          </div>
        </div>
      </Modal.Footer>
    </Modal>
  );
};

export default UmrahAdditionalDiscount;
